/**
 * AgentsFlow Context
 * Global state management for the AgentsFlow module
 */

import React, { createContext, useContext, useReducer, useCallback, useState } from 'react';
import { EXPERIENCE_LEVELS, CAPABILITIES, BUSINESS_TYPES } from '../constants';

// ===============================================
// INITIAL STATE
// ===============================================

const initialState = {
  // Current agent being created/edited
  currentAgent: null,

  // Creation wizard state
  wizard: {
    currentStep: 1,
    totalSteps: 4,
    businessType: null,
    businessInfo: {
      name: '',
      agentName: '',
      address: '',
      workingHours: '',
      phone: '',
      email: '',
    },
    selectedCapabilities: [],
    experienceLevel: EXPERIENCE_LEVELS.QUICK,
  },

  // Editor state (Level 2)
  editor: {
    activeTab: 'identity',
    hasUnsavedChanges: false,
    identity: {},
    capabilities: [],
    messages: {},
    faq: [],
    behavior: {},
    integrations: [],
    knowledge: [],
    // New fields for expanded features
    llm_config: {
      provider: 'openai',
      model: 'gpt-4o',
      max_tokens: 4096,
      fallback_enabled: true,
    },
    advanced_config: {},
    notification_rules: [],
    products: [],
    // New config fields
    identity_config: {
      display_name: '',
      role: '',
      personality_traits: [],
      tone: 'professional',
      gender: 'neutral',
      avatar_url: '',
    },
    business_info: {
      business_name: '',
      address: '',
      city: '',
      state: '',
      phone: '',
      email: '',
      website: '',
      working_hours: {},
      payment_methods: [],
    },
    behavior_config: {
      rules: [],
      transfer_conditions: ['client_request', 'complaint'],
      max_attempts_before_transfer: 3,
    },
    humanization_config: {
      enabled: true,
      modules: {
        anti_robot_filter: { enabled: true },
        casual_writing: { enabled: true, typo_enabled: false, typo_rate: 0.0 },
        relaxed_tone: { enabled: true },
        quick_responses: { enabled: true },
        avoid_closing_questions: { enabled: false },
        message_splitter: { enabled: true, max_messages: 5 },
        natural_emojis: { enabled: true, probability: 0.3, max_per_interaction: 2 },
        conversation_flow: { enabled: true },
        typing_rhythm: { enabled: true, min_delay_ms: 500, max_delay_ms: 3000, chars_per_second: 30 },
      },
    },
    agent_contacts: [],
    sales_config: {
      enabled: false,
      primary_technique: 'consultative',
      strategies: [],
      intensity: 3,
      max_attempts: 2,
      respect_no: true,
    },
    custom_messages: {},
  },

  // Agent list
  agents: [],
  agentsLoading: false,
  agentsError: null,

  // Templates
  templates: [],
  templatesLoading: false,

  // Available capabilities catalog
  capabilitiesCatalog: Object.values(CAPABILITIES),

  // Vault keys (single source of truth shared by ApiKeysTab and LLMConfigTab)
  vaultKeys: [],

  // Simulator state
  simulator: {
    isOpen: false,
    messages: [],
    isTyping: false,
    debugMode: false,
    showCapabilities: true,
    showVariables: false,
  },

  // UI State
  ui: {
    isSaving: false,
    isPublishing: false,
    showSuccessModal: false,
    errorMessage: null,
  },
};

// ===============================================
// ACTION TYPES
// ===============================================

const ActionTypes = {
  // Wizard actions
  SET_WIZARD_STEP: 'SET_WIZARD_STEP',
  SET_BUSINESS_TYPE: 'SET_BUSINESS_TYPE',
  SET_BUSINESS_INFO: 'SET_BUSINESS_INFO',
  TOGGLE_CAPABILITY: 'TOGGLE_CAPABILITY',
  SET_SELECTED_CAPABILITIES: 'SET_SELECTED_CAPABILITIES',
  RESET_WIZARD: 'RESET_WIZARD',

  // Editor actions
  SET_ACTIVE_TAB: 'SET_ACTIVE_TAB',
  SET_EDITOR_DATA: 'SET_EDITOR_DATA',
  UPDATE_IDENTITY: 'UPDATE_IDENTITY',
  UPDATE_CAPABILITIES: 'UPDATE_CAPABILITIES',
  UPDATE_MESSAGES: 'UPDATE_MESSAGES',
  UPDATE_FAQ: 'UPDATE_FAQ',
  UPDATE_BEHAVIOR: 'UPDATE_BEHAVIOR',
  UPDATE_INTEGRATIONS: 'UPDATE_INTEGRATIONS',
  UPDATE_KNOWLEDGE: 'UPDATE_KNOWLEDGE',
  UPDATE_LLM_CONFIG: 'UPDATE_LLM_CONFIG',
  UPDATE_ADVANCED_CONFIG: 'UPDATE_ADVANCED_CONFIG',
  UPDATE_NOTIFICATION_RULES: 'UPDATE_NOTIFICATION_RULES',
  UPDATE_PRODUCTS: 'UPDATE_PRODUCTS',
  UPDATE_IDENTITY_CONFIG: 'UPDATE_IDENTITY_CONFIG',
  UPDATE_BUSINESS_INFO: 'UPDATE_BUSINESS_INFO',
  UPDATE_BEHAVIOR_CONFIG: 'UPDATE_BEHAVIOR_CONFIG',
  UPDATE_HUMANIZATION_CONFIG: 'UPDATE_HUMANIZATION_CONFIG',
  UPDATE_SALES_CONFIG: 'UPDATE_SALES_CONFIG',
  UPDATE_AGENT_CONTACTS: 'UPDATE_AGENT_CONTACTS',
  SET_UNSAVED_CHANGES: 'SET_UNSAVED_CHANGES',

  // Agent actions
  SET_CURRENT_AGENT: 'SET_CURRENT_AGENT',
  SET_AGENTS: 'SET_AGENTS',
  SET_AGENTS_LOADING: 'SET_AGENTS_LOADING',
  SET_AGENTS_ERROR: 'SET_AGENTS_ERROR',
  ADD_AGENT: 'ADD_AGENT',
  UPDATE_AGENT: 'UPDATE_AGENT',
  REMOVE_AGENT: 'REMOVE_AGENT',

  // Templates actions
  SET_TEMPLATES: 'SET_TEMPLATES',
  SET_TEMPLATES_LOADING: 'SET_TEMPLATES_LOADING',

  // Simulator actions
  TOGGLE_SIMULATOR: 'TOGGLE_SIMULATOR',
  ADD_SIMULATOR_MESSAGE: 'ADD_SIMULATOR_MESSAGE',
  SET_SIMULATOR_TYPING: 'SET_SIMULATOR_TYPING',
  CLEAR_SIMULATOR: 'CLEAR_SIMULATOR',
  SET_SIMULATOR_OPTIONS: 'SET_SIMULATOR_OPTIONS',

  // UI actions
  SET_SAVING: 'SET_SAVING',
  SET_PUBLISHING: 'SET_PUBLISHING',
  SET_SUCCESS_MODAL: 'SET_SUCCESS_MODAL',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',

  // Vault actions
  SET_VAULT_KEYS: 'SET_VAULT_KEYS',
};

// ===============================================
// REDUCER
// ===============================================

function agentsFlowReducer(state, action) {
  switch (action.type) {
    // Wizard actions
    case ActionTypes.SET_WIZARD_STEP:
      return {
        ...state,
        wizard: { ...state.wizard, currentStep: action.payload },
      };

    case ActionTypes.SET_BUSINESS_TYPE:
      const businessType = BUSINESS_TYPES.find((bt) => bt.id === action.payload);
      return {
        ...state,
        wizard: {
          ...state.wizard,
          businessType: action.payload,
          selectedCapabilities: businessType?.defaultCapabilities || [],
        },
      };

    case ActionTypes.SET_BUSINESS_INFO:
      return {
        ...state,
        wizard: {
          ...state.wizard,
          businessInfo: { ...state.wizard.businessInfo, ...action.payload },
        },
      };

    case ActionTypes.TOGGLE_CAPABILITY:
      const capCode = action.payload;
      const isSelected = state.wizard.selectedCapabilities.includes(capCode);
      return {
        ...state,
        wizard: {
          ...state.wizard,
          selectedCapabilities: isSelected
            ? state.wizard.selectedCapabilities.filter((c) => c !== capCode)
            : [...state.wizard.selectedCapabilities, capCode],
        },
      };

    case ActionTypes.SET_SELECTED_CAPABILITIES:
      return {
        ...state,
        wizard: { ...state.wizard, selectedCapabilities: action.payload },
      };

    case ActionTypes.RESET_WIZARD:
      return {
        ...state,
        wizard: initialState.wizard,
      };

    // Editor actions
    case ActionTypes.SET_ACTIVE_TAB:
      return {
        ...state,
        editor: { ...state.editor, activeTab: action.payload },
      };

    case ActionTypes.SET_EDITOR_DATA:
      return {
        ...state,
        editor: { ...state.editor, ...action.payload, hasUnsavedChanges: false },
      };

    case ActionTypes.UPDATE_IDENTITY:
      return {
        ...state,
        editor: {
          ...state.editor,
          identity: { ...state.editor.identity, ...action.payload },
          hasUnsavedChanges: true,
        },
      };

    case ActionTypes.UPDATE_CAPABILITIES:
      return {
        ...state,
        editor: {
          ...state.editor,
          capabilities: action.payload,
          hasUnsavedChanges: true,
        },
      };

    case ActionTypes.UPDATE_MESSAGES:
      return {
        ...state,
        editor: {
          ...state.editor,
          messages: { ...state.editor.messages, ...action.payload },
          hasUnsavedChanges: true,
        },
      };

    case ActionTypes.UPDATE_FAQ:
      return {
        ...state,
        editor: {
          ...state.editor,
          faq: action.payload,
          hasUnsavedChanges: true,
        },
      };

    case ActionTypes.UPDATE_BEHAVIOR:
      return {
        ...state,
        editor: {
          ...state.editor,
          behavior: { ...state.editor.behavior, ...action.payload },
          hasUnsavedChanges: true,
        },
      };

    case ActionTypes.UPDATE_INTEGRATIONS:
      return {
        ...state,
        editor: {
          ...state.editor,
          integrations: action.payload,
          hasUnsavedChanges: true,
        },
      };

    case ActionTypes.UPDATE_KNOWLEDGE:
      return {
        ...state,
        editor: {
          ...state.editor,
          knowledge: action.payload,
          hasUnsavedChanges: true,
        },
      };

    case ActionTypes.UPDATE_LLM_CONFIG:
      return {
        ...state,
        editor: {
          ...state.editor,
          llm_config: { ...state.editor.llm_config, ...action.payload },
          hasUnsavedChanges: true,
        },
      };

    case ActionTypes.UPDATE_ADVANCED_CONFIG:
      return {
        ...state,
        editor: {
          ...state.editor,
          advanced_config: { ...state.editor.advanced_config, ...action.payload },
          hasUnsavedChanges: true,
        },
      };

    case ActionTypes.UPDATE_NOTIFICATION_RULES:
      return {
        ...state,
        editor: {
          ...state.editor,
          notification_rules: action.payload,
          hasUnsavedChanges: true,
        },
      };

    case ActionTypes.UPDATE_PRODUCTS:
      return {
        ...state,
        editor: {
          ...state.editor,
          products: action.payload,
          hasUnsavedChanges: true,
        },
      };

    case ActionTypes.UPDATE_IDENTITY_CONFIG:
      return {
        ...state,
        editor: {
          ...state.editor,
          identity_config: { ...state.editor.identity_config, ...action.payload },
          hasUnsavedChanges: true,
        },
      };

    case ActionTypes.UPDATE_BUSINESS_INFO:
      return {
        ...state,
        editor: {
          ...state.editor,
          business_info: { ...state.editor.business_info, ...action.payload },
          hasUnsavedChanges: true,
        },
      };

    case ActionTypes.UPDATE_BEHAVIOR_CONFIG:
      return {
        ...state,
        editor: {
          ...state.editor,
          behavior_config: { ...state.editor.behavior_config, ...action.payload },
          hasUnsavedChanges: true,
        },
      };

    case ActionTypes.UPDATE_HUMANIZATION_CONFIG:
      return {
        ...state,
        editor: {
          ...state.editor,
          humanization_config: { ...state.editor.humanization_config, ...action.payload },
          hasUnsavedChanges: true,
        },
      };

    case ActionTypes.UPDATE_SALES_CONFIG:
      return {
        ...state,
        editor: {
          ...state.editor,
          sales_config: { ...state.editor.sales_config, ...action.payload },
          hasUnsavedChanges: true,
        },
      };

    case ActionTypes.UPDATE_AGENT_CONTACTS:
      return {
        ...state,
        editor: {
          ...state.editor,
          agent_contacts: action.payload,
          hasUnsavedChanges: true,
        },
      };

    case ActionTypes.SET_UNSAVED_CHANGES:
      return {
        ...state,
        editor: { ...state.editor, hasUnsavedChanges: action.payload },
      };

    // Agent actions
    case ActionTypes.SET_CURRENT_AGENT:
      return { ...state, currentAgent: action.payload };

    case ActionTypes.SET_AGENTS:
      return { ...state, agents: action.payload, agentsLoading: false };

    case ActionTypes.SET_AGENTS_LOADING:
      return { ...state, agentsLoading: action.payload };

    case ActionTypes.SET_AGENTS_ERROR:
      return { ...state, agentsError: action.payload, agentsLoading: false };

    case ActionTypes.ADD_AGENT:
      return { ...state, agents: [action.payload, ...state.agents] };

    case ActionTypes.UPDATE_AGENT:
      return {
        ...state,
        agents: state.agents.map((a) =>
          a.id === action.payload.id ? { ...a, ...action.payload } : a
        ),
        currentAgent:
          state.currentAgent?.id === action.payload.id
            ? { ...state.currentAgent, ...action.payload }
            : state.currentAgent,
      };

    case ActionTypes.REMOVE_AGENT:
      return {
        ...state,
        agents: state.agents.filter((a) => a.id !== action.payload),
      };

    // Templates actions
    case ActionTypes.SET_TEMPLATES:
      return { ...state, templates: action.payload, templatesLoading: false };

    case ActionTypes.SET_TEMPLATES_LOADING:
      return { ...state, templatesLoading: action.payload };

    // Simulator actions
    case ActionTypes.TOGGLE_SIMULATOR:
      return {
        ...state,
        simulator: {
          ...state.simulator,
          isOpen: action.payload !== undefined ? action.payload : !state.simulator.isOpen,
        },
      };

    case ActionTypes.ADD_SIMULATOR_MESSAGE:
      return {
        ...state,
        simulator: {
          ...state.simulator,
          messages: [...state.simulator.messages, action.payload],
        },
      };

    case ActionTypes.SET_SIMULATOR_TYPING:
      return {
        ...state,
        simulator: { ...state.simulator, isTyping: action.payload },
      };

    case ActionTypes.CLEAR_SIMULATOR:
      return {
        ...state,
        simulator: { ...state.simulator, messages: [] },
      };

    case ActionTypes.SET_SIMULATOR_OPTIONS:
      return {
        ...state,
        simulator: { ...state.simulator, ...action.payload },
      };

    // UI actions
    case ActionTypes.SET_SAVING:
      return { ...state, ui: { ...state.ui, isSaving: action.payload } };

    case ActionTypes.SET_PUBLISHING:
      return { ...state, ui: { ...state.ui, isPublishing: action.payload } };

    case ActionTypes.SET_SUCCESS_MODAL:
      return { ...state, ui: { ...state.ui, showSuccessModal: action.payload } };

    case ActionTypes.SET_ERROR:
      return { ...state, ui: { ...state.ui, errorMessage: action.payload } };

    case ActionTypes.CLEAR_ERROR:
      return { ...state, ui: { ...state.ui, errorMessage: null } };

    case ActionTypes.SET_VAULT_KEYS:
      return { ...state, vaultKeys: action.payload };

    default:
      return state;
  }
}

// ===============================================
// CONTEXT
// ===============================================

const AgentsFlowContext = createContext(null);

// ===============================================
// PROVIDER
// ===============================================

export function AgentsFlowProvider({ children }) {
  const [state, dispatch] = useReducer(agentsFlowReducer, initialState);

  // Ephemeral highlight — not part of reducer (no undo/redo needed)
  const [highlightedNodeId, setHighlightedNodeId] = useState(null);

  // ===============================================
  // WIZARD ACTIONS
  // ===============================================

  const setWizardStep = useCallback((step) => {
    dispatch({ type: ActionTypes.SET_WIZARD_STEP, payload: step });
  }, []);

  const setBusinessType = useCallback((typeId) => {
    dispatch({ type: ActionTypes.SET_BUSINESS_TYPE, payload: typeId });
  }, []);

  const setBusinessInfo = useCallback((info) => {
    dispatch({ type: ActionTypes.SET_BUSINESS_INFO, payload: info });
  }, []);

  const toggleCapability = useCallback((capabilityCode) => {
    dispatch({ type: ActionTypes.TOGGLE_CAPABILITY, payload: capabilityCode });
  }, []);

  const setSelectedCapabilities = useCallback((capabilities) => {
    dispatch({ type: ActionTypes.SET_SELECTED_CAPABILITIES, payload: capabilities });
  }, []);

  const resetWizard = useCallback(() => {
    dispatch({ type: ActionTypes.RESET_WIZARD });
  }, []);

  const nextStep = useCallback(() => {
    if (state.wizard.currentStep < state.wizard.totalSteps) {
      dispatch({ type: ActionTypes.SET_WIZARD_STEP, payload: state.wizard.currentStep + 1 });
    }
  }, [state.wizard.currentStep, state.wizard.totalSteps]);

  const prevStep = useCallback(() => {
    if (state.wizard.currentStep > 1) {
      dispatch({ type: ActionTypes.SET_WIZARD_STEP, payload: state.wizard.currentStep - 1 });
    }
  }, [state.wizard.currentStep]);

  // ===============================================
  // EDITOR ACTIONS
  // ===============================================

  const setActiveTab = useCallback((tabId) => {
    dispatch({ type: ActionTypes.SET_ACTIVE_TAB, payload: tabId });
  }, []);

  const setEditorData = useCallback((data) => {
    dispatch({ type: ActionTypes.SET_EDITOR_DATA, payload: data });
  }, []);

  const updateIdentity = useCallback((data) => {
    dispatch({ type: ActionTypes.UPDATE_IDENTITY, payload: data });
  }, []);

  const updateCapabilities = useCallback((data) => {
    dispatch({ type: ActionTypes.UPDATE_CAPABILITIES, payload: data });
  }, []);

  const updateMessages = useCallback((data) => {
    dispatch({ type: ActionTypes.UPDATE_MESSAGES, payload: data });
  }, []);

  const updateFAQ = useCallback((data) => {
    dispatch({ type: ActionTypes.UPDATE_FAQ, payload: data });
  }, []);

  const updateBehavior = useCallback((data) => {
    dispatch({ type: ActionTypes.UPDATE_BEHAVIOR, payload: data });
  }, []);

  const updateIntegrations = useCallback((data) => {
    dispatch({ type: ActionTypes.UPDATE_INTEGRATIONS, payload: data });
  }, []);

  const updateKnowledge = useCallback((data) => {
    dispatch({ type: ActionTypes.UPDATE_KNOWLEDGE, payload: data });
  }, []);

  const updateLLMConfig = useCallback((data) => {
    dispatch({ type: ActionTypes.UPDATE_LLM_CONFIG, payload: data });
  }, []);

  const updateAdvancedConfig = useCallback((data) => {
    dispatch({ type: ActionTypes.UPDATE_ADVANCED_CONFIG, payload: data });
  }, []);

  const updateNotificationRules = useCallback((data) => {
    dispatch({ type: ActionTypes.UPDATE_NOTIFICATION_RULES, payload: data });
  }, []);

  const updateProducts = useCallback((data) => {
    dispatch({ type: ActionTypes.UPDATE_PRODUCTS, payload: data });
  }, []);

  const updateIdentityConfig = useCallback((data) => {
    dispatch({ type: ActionTypes.UPDATE_IDENTITY_CONFIG, payload: data });
  }, []);

  const updateBusinessInfo = useCallback((data) => {
    dispatch({ type: ActionTypes.UPDATE_BUSINESS_INFO, payload: data });
  }, []);

  const updateBehaviorConfig = useCallback((data) => {
    dispatch({ type: ActionTypes.UPDATE_BEHAVIOR_CONFIG, payload: data });
  }, []);

  const updateHumanizationConfig = useCallback((data) => {
    dispatch({ type: ActionTypes.UPDATE_HUMANIZATION_CONFIG, payload: data });
  }, []);

  const updateSalesConfig = useCallback((data) => {
    dispatch({ type: ActionTypes.UPDATE_SALES_CONFIG, payload: data });
  }, []);

  const updateAgentContacts = useCallback((data) => {
    dispatch({ type: ActionTypes.UPDATE_AGENT_CONTACTS, payload: data });
  }, []);

  const setUnsavedChanges = useCallback((value) => {
    dispatch({ type: ActionTypes.SET_UNSAVED_CHANGES, payload: value });
  }, []);

  // ===============================================
  // AGENT ACTIONS
  // ===============================================

  const setCurrentAgent = useCallback((agent) => {
    dispatch({ type: ActionTypes.SET_CURRENT_AGENT, payload: agent });
  }, []);

  const setAgents = useCallback((agents) => {
    dispatch({ type: ActionTypes.SET_AGENTS, payload: agents });
  }, []);

  const setAgentsLoading = useCallback((loading) => {
    dispatch({ type: ActionTypes.SET_AGENTS_LOADING, payload: loading });
  }, []);

  const setAgentsError = useCallback((error) => {
    dispatch({ type: ActionTypes.SET_AGENTS_ERROR, payload: error });
  }, []);

  const addAgent = useCallback((agent) => {
    dispatch({ type: ActionTypes.ADD_AGENT, payload: agent });
  }, []);

  const updateAgent = useCallback((agent) => {
    dispatch({ type: ActionTypes.UPDATE_AGENT, payload: agent });
  }, []);

  const removeAgent = useCallback((agentId) => {
    dispatch({ type: ActionTypes.REMOVE_AGENT, payload: agentId });
  }, []);

  // ===============================================
  // TEMPLATES ACTIONS
  // ===============================================

  const setTemplates = useCallback((templates) => {
    dispatch({ type: ActionTypes.SET_TEMPLATES, payload: templates });
  }, []);

  const setTemplatesLoading = useCallback((loading) => {
    dispatch({ type: ActionTypes.SET_TEMPLATES_LOADING, payload: loading });
  }, []);

  // ===============================================
  // SIMULATOR ACTIONS
  // ===============================================

  const toggleSimulator = useCallback((value) => {
    dispatch({ type: ActionTypes.TOGGLE_SIMULATOR, payload: value });
  }, []);

  const addSimulatorMessage = useCallback((message) => {
    dispatch({ type: ActionTypes.ADD_SIMULATOR_MESSAGE, payload: message });
  }, []);

  const setSimulatorTyping = useCallback((typing) => {
    dispatch({ type: ActionTypes.SET_SIMULATOR_TYPING, payload: typing });
  }, []);

  const clearSimulator = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_SIMULATOR });
  }, []);

  const setSimulatorOptions = useCallback((options) => {
    dispatch({ type: ActionTypes.SET_SIMULATOR_OPTIONS, payload: options });
  }, []);

  // ===============================================
  // UI ACTIONS
  // ===============================================

  const setSaving = useCallback((saving) => {
    dispatch({ type: ActionTypes.SET_SAVING, payload: saving });
  }, []);

  const setPublishing = useCallback((publishing) => {
    dispatch({ type: ActionTypes.SET_PUBLISHING, payload: publishing });
  }, []);

  const setSuccessModal = useCallback((show) => {
    dispatch({ type: ActionTypes.SET_SUCCESS_MODAL, payload: show });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: ActionTypes.SET_ERROR, payload: error });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_ERROR });
  }, []);

  // ===============================================
  // VAULT ACTIONS
  // ===============================================

  const setVaultKeys = useCallback((keys) => {
    dispatch({ type: ActionTypes.SET_VAULT_KEYS, payload: keys });
  }, []);

  // ===============================================
  // CONTEXT VALUE
  // ===============================================

  const value = {
    state,
    // Wizard
    setWizardStep,
    setBusinessType,
    setBusinessInfo,
    toggleCapability,
    setSelectedCapabilities,
    resetWizard,
    nextStep,
    prevStep,
    // Editor
    setActiveTab,
    setEditorData,
    updateIdentity,
    updateCapabilities,
    updateMessages,
    updateFAQ,
    updateBehavior,
    updateIntegrations,
    updateKnowledge,
    updateLLMConfig,
    updateAdvancedConfig,
    updateNotificationRules,
    updateProducts,
    updateIdentityConfig,
    updateBusinessInfo,
    updateBehaviorConfig,
    updateHumanizationConfig,
    updateSalesConfig,
    updateAgentContacts,
    setUnsavedChanges,
    // Agents
    setCurrentAgent,
    setAgents,
    setAgentsLoading,
    setAgentsError,
    addAgent,
    updateAgent,
    removeAgent,
    // Templates
    setTemplates,
    setTemplatesLoading,
    // Simulator
    toggleSimulator,
    addSimulatorMessage,
    setSimulatorTyping,
    clearSimulator,
    setSimulatorOptions,
    // UI
    setSaving,
    setPublishing,
    setSuccessModal,
    setError,
    clearError,
    // Vault
    setVaultKeys,
    // Canvas highlight
    highlightedNodeId,
    setHighlightedNodeId,
  };

  return (
    <AgentsFlowContext.Provider value={value}>{children}</AgentsFlowContext.Provider>
  );
}

// ===============================================
// HOOK
// ===============================================

export function useAgentsFlow() {
  const context = useContext(AgentsFlowContext);
  if (!context) {
    throw new Error('useAgentsFlow must be used within an AgentsFlowProvider');
  }
  return context;
}

export default AgentsFlowContext;
